<template>
    <tr>
        <td>
            <div class="product-item">
                <router-link class="product-thumb" target="_blank" :to="`/product/${product._id}`">
                    <img v-lazy="product.firstCoverPhotoUrl" alt="Product"/>
                </router-link>
                <div class="product-info">
                    <h4 class="product-title">
                        <router-link target="_blank" :to="`/product/${product._id}`">{{this.product.title}}</router-link>
                    </h4>
                    <span v-if="product.isbn"><em>{{$t("checkout.review.checkoutReviewCartDetailsLine.isbn")}}:</em> {{this.product.isbn}}</span>
                    <span v-if="product.publisher._id"><em>{{$t("checkout.review.checkoutReviewCartDetailsLine.publisher")}}:</em> {{this.product.publisher.name}}</span>
                    <span v-if="product.author"><em>{{$t("checkout.review.checkoutReviewCartDetailsLine.authors")}}:</em> {{this.product.author}}</span>
                    <hr class="my-2"/>
                    <span v-if="!!physicalPrintingApproval">
                        <em v-html="$t('checkout.review.checkoutReviewCartDetailsLine.physicalPrintingApprovalOptionTaken')"/>
                        <span v-html="`(+ ${properPrice(withoutTax ? physicalPrintingApproval.charge.price : physicalPrintingApproval.charge.priceWT, physicalPrintingApproval.charge.currency, currencies)})`"/>
                    </span>
                    <span v-if="!!assistedProductCreationOption">
                        <em v-html="$t('checkout.review.checkoutReviewCartDetailsLine.assistedProductCreationOptionTaken')"/>
                        <span v-html="`(+ ${properPrice(withoutTax ? assistedProductCreationOption.charge.price : assistedProductCreationOption.charge.priceWT, assistedProductCreationOption.charge.currency, currencies)})`"/>
                    </span>
<!--                    <span v-if="product.customization.dedication"><em>{{$t("checkout.review.checkoutReviewCartDetailsLine.dedication")}}:</em> {{this.product.customization.dedication}}</span>-->
                    <CustomizeProductBtn :product="product" class="btn btn-primary btn-xs mt-2" @show-customize-product-modal="showCustomizeProductModal"/>
                </div>
            </div>
        </td>
        <td class="text-center text-lg text-medium">
            <span v-if="productFromReview && currentPrice !== originalPrice">
                <del class="text-sm text-muted"
                     v-tooltip="$t(withoutTax ? 'OrderPricing.lastPriceHT' : 'OrderReviewCartDetailsLine.lastPriceWTWasPriceWT', { priceWT: properPrice(originalPrice, currency, currencies) })">
                    {{ properPrice(originalPrice, currency, currencies) }}
                </del>
                <i class="fa fa-arrow-right text-muted ml-1 mr-1 text-sm"/>
            </span>
            <span v-html="productPriceWT"/>
        </td>
        <td class="text-center text-lg text-medium">
            <span v-if="productFromReview && productFromReview.discount !== product.discount">
                <del class="text-sm text-muted"
                     v-tooltip="$t('OrderReviewCartDetailsLine.lastDiscountWasDiscount', { discount: product.discount })">
                    {{ product.discount }} %
                </del>
                <i class="fa fa-arrow-right text-muted ml-1 mr-1 text-sm"/>
            </span>
            <span v-html="productDiscount"/>
        </td>
        <td class="text-center text-lg text-medium">
            <span v-if="productFromReview && currentDiscountedPrice !== originalDiscountedPrice">
                <del class="text-sm text-muted"
                     v-tooltip="$t(withoutTax ? 'OrderPricing.lastDiscountedPriceHT' : 'OrderReviewCartDetailsLine.lastDiscountedPriceWTWasDiscountedPriceWT', { discountedPriceWT: properPrice(originalDiscountedPrice, currency, currencies) })">
                    {{ properPrice(originalDiscountedPrice, currency, currencies) }}
                </del>
                <i class="fa fa-arrow-right text-muted ml-1 mr-1 text-sm"/>
            </span>
            <span v-html="productDiscountedPriceWT"/>
        </td>
        <td class="text-center text-lg text-medium">{{this.product.quantity}}</td>
        <td class="text-center text-lg text-medium white-space-no-wrap">
            <span v-if="productFromReview && getTotalWT(productFromReview) !== getTotalWT(product)">
                <del class="text-sm text-muted"
                     v-tooltip="$t(withoutTax ? 'OrderPricing.lastTotalHT' : 'OrderReviewCartDetailsLine.lastTotalWTWasTotalWT', { totalWT: properPrice(getTotalWT(product), currency, currencies) })">
                    {{ properPrice(getTotalWT(product), currency, currencies) }}
                </del>
                <i class="fa fa-arrow-right text-muted ml-1 mr-1 text-sm"/>
            </span>
            <span v-html="productTotalWT"/>
        </td>
    </tr>
</template>

<script>
import { mapGetters } from "vuex";
import { properPrice } from "@/helpers/price";
import { getProductTotal } from "@/helpers/order-pricing";
import CustomizeProductBtn from "../../../Product/CustomizeProductBtn/CustomizeProductBtn";

export default {
    name: "OrderReviewCartDetailsLine",
    components: {CustomizeProductBtn},
    props: {
        withoutTax: {
            type: Boolean,
            default: false,
        },
        product: {
            type: Object,
            required: true,
        },
        review: {
            type: Object,
            default: () => null,
        },
        currency: {
            type: String,
            required: true,
        },
    },
    computed: {
        ...mapGetters("currencies", {
            currencies: "currencies",
        }),
        productFromReview() {
            return this.review ? this.review.products.find(product => product._id === this.product._id) : null;
        },
        originalPrice() {
            return this.withoutTax ? this.product.price : this.product.priceWT;
        },
        originalDiscountedPrice() {
            return this.withoutTax ? this.product.discountedPrice : this.product.discountedPriceWT;
        },
        currentPrice() {
            const product = this.productFromReview || this.product;
            return this.withoutTax ? product.price : product.priceWT;
        },
        currentDiscountedPrice() {
            const product = this.productFromReview || this.product;
            return this.withoutTax ? product.discountedPrice : product.discountedPriceWT;
        },
        productPriceWT() {
            return this.properPrice(this.currentPrice, this.currency, this.currencies);
        },
        productDiscount() {
            const product = this.productFromReview ? this.productFromReview : this.product;
            return `${product.discount} %`;
        },
        productDiscountedPriceWT() {
            return this.properPrice(this.currentDiscountedPrice, this.currency, this.currencies);
        },
        productTotalWT() {
            const product = this.productFromReview ? this.productFromReview : this.product;
            const totalWT = this.getTotalWT(product);
            return this.properPrice(totalWT, this.currency, this.currencies);
        },
        assistedProductCreationOption() {
            return !this.product.alreadyOrdered && (this.productFromReview || this.product).getOption("assisted-product-creation");
        },
        physicalPrintingApproval() {
            return !this.product.alreadyOrdered && (this.productFromReview || this.product).getOption("physical-printing-approval");
        },
    },
    methods: {
        properPrice,
        getTotalWT(product) {
            return getProductTotal(product, this.withoutTax);
        },
        showCustomizeProductModal(product) {
            this.$emit("show-customize-product-modal", product);
        },
    },
};
</script>

<style lang="scss" scoped>
</style>